import { useState, useEffect, useCallback } from 'react'
import { binService } from '@/lib/api/services/bin'

export interface BinCountState {
  total: number
  templates: number
  assets: number
  loading: boolean
  error: string | null
}

/**
 * Hook to fetch and cache bin item count
 * Refreshes every 5 minutes or on manual refresh
 */
export function useBinCount() {
  const [state, setState] = useState<BinCountState>({
    total: 0,
    templates: 0,
    assets: 0,
    loading: true,
    error: null,
  })

  const fetchCount = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }))
      const count = await binService.getCount()
      setState({
        total: count.total,
        templates: count.templates,
        assets: count.assets,
        loading: false,
        error: null,
      })
    } catch (err) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch bin count',
      }))
    }
  }, [])

  useEffect(() => {
    fetchCount()

    // Refresh every 5 minutes
    const interval = setInterval(fetchCount, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [fetchCount])

  return {
    ...state,
    refresh: fetchCount,
  }
}
